<div class="space-y-6">
  <div>
    <h2 class="mb-2 text-xl font-semibold text-gray-900">
      {{ "contribute.adjustContribution.title" | transloco }}
    </h2>
    <p class="text-gray-600">
      {{ "contribute.adjustContribution.description" | transloco }}
    </p>
  </div>

  <!-- Two Column Layout -->
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-5">
    <!-- Left Column: Controls -->
    <div class="space-y-6 lg:col-span-3">
      <!-- Coverage Toggle -->
      <div class="rounded-lg border bg-gray-50 p-4">
        <label for="coverage" class="mb-2 block font-medium text-gray-700">
          {{ "contribute.adjustContribution.coverage" | transloco }}
        </label>
        <p-selectButton
          [options]="coverageOptions"
          [(ngModel)]="isSingleTraveler"
          (onChange)="onCoverageChange($event.value)"
          optionLabel="label"
          optionValue="value"
        />
        <p class="mt-2 text-sm text-gray-500">
          @if (isSingleTraveler) {
            {{ "contribute.adjustContribution.singleTravelerHint" | transloco }}
            ({{ wizardService.trip()?.travellers }}
            {{ "contribute.adjustContribution.travelers" | transloco }})
          } @else {
            {{ "contribute.adjustContribution.fullTripHint" | transloco }}
          }
        </p>
      </div>

      <!-- Percentage Slider / Input -->
      <div class="rounded-lg border bg-gray-50 p-4">
        <div class="mb-3 flex items-center justify-between">
          <label for="percentage" class="font-medium text-gray-700">
            {{ "contribute.adjustContribution.percentage" | transloco }}
          </label>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-500">{{ "contribute.adjustContribution.advancedMode" | transloco }}</span>
            <p-inputSwitch [(ngModel)]="advancedMode" />
          </div>
        </div>

        @if (advancedMode) {
          <!-- Advanced Mode: Input Number -->
          <div class="flex items-center gap-3">
            <p-inputNumber
              [(ngModel)]="percentage"
              (onInput)="onInputChange($event)"
              [min]="0"
              [max]="9999"
              suffix="%"
              [showButtons]="true"
              buttonLayout="horizontal"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
              inputStyleClass="w-24 text-center font-semibold text-primary-600"
            />
            <span class="text-sm text-gray-500">{{ "contribute.adjustContribution.advancedModeHint" | transloco }}</span>
          </div>
        } @else {
          <!-- Standard Mode: Slider -->
          <p-slider
            [(ngModel)]="percentage"
            (onChange)="onSliderChange($event)"
            [min]="0"
            [max]="200"
            [step]="5"
            styleClass="w-full"
          />
          <div class="mt-2 flex justify-between text-sm text-gray-500">
            <span>0%</span>
            <span>100%</span>
            <span>200%</span>
          </div>
        }

        @if (percentage > 100) {
          <p class="mt-2 text-sm text-primary-600">
            {{ "contribute.adjustContribution.overContributionHint" | transloco }}
          </p>
        }
      </div>

      <!-- VAT Number Input (Optional) -->
      <div class="rounded-lg border bg-gray-50 p-4">
        <label for="vatNumber" class="mb-2 block font-medium text-gray-700">
          {{ "contribute.vat.label" | transloco }}
          <span class="font-normal text-gray-500">({{ "common.optional" | transloco }})</span>
        </label>
        <p class="mb-3 text-sm text-gray-500">
          {{ "contribute.vat.hint" | transloco }}
        </p>
        <div class="relative">
          <input
            id="vatNumber"
            type="text"
            pInputText
            class="w-full"
            [ngModel]="vatNumberInput"
            (ngModelChange)="onVatNumberChange($event)"
            placeholder="FR12345678901"
            [class.ng-invalid]="wizardService.vatValidation()?.isValid === false && vatNumberInput"
            [class.ng-dirty]="vatNumberInput"
          />
          @if (wizardService.isVatValidationLoading()) {
            <div class="absolute right-3 top-1/2 -translate-y-1/2">
              <i class="pi pi-spin pi-spinner text-gray-400"></i>
            </div>
          }
        </div>

        <!-- Validation feedback -->
        @if (vatNumberInput && !wizardService.isVatValidationLoading()) {
          @if (wizardService.vatValidation(); as validation) {
            @if (validation.isValid) {
              <div class="mt-2 flex items-start gap-2 text-sm text-green-600">
                <i class="pi pi-check-circle mt-0.5"></i>
                <div>
                  <p>{{ "contribute.vat.valid" | transloco }}</p>
                  @if (validation.companyName) {
                    <p class="text-gray-600">{{ validation.companyName }}</p>
                  }
                  @if (validation.isReverseChargeEligible) {
                    <p class="mt-1 font-medium text-blue-600">
                      {{ "contribute.vat.reverseChargeEligible" | transloco }}
                    </p>
                  }
                </div>
              </div>
            } @else {
              <div class="mt-2 flex items-start gap-2 text-sm text-red-600">
                <i class="pi pi-times-circle mt-0.5"></i>
                <p>{{ "contribute.vat.invalid" | transloco }}</p>
              </div>
            }
          }
        }
      </div>
    </div>

    <!-- Right Column: Order Summary (Sticky) - Same style as Step 4 -->
    <div class="lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
      <div class="overflow-hidden rounded-lg border border-greyscale-200">
        <!-- Header -->
        <div class="bg-primary-100 p-4">
          <h3 class="font-semibold text-primary-700">{{ "contribute.reviewConfirm.pricingSummary" | transloco }}</h3>
        </div>
        <!-- Content -->
        <div class="space-y-3 bg-greyscale-50 p-4">
          <div class="flex items-center justify-between">
            <span class="text-gray-600">{{ "contribute.summary.baseCO2" | transloco }}</span>
            <span class="font-medium">{{ wizardService.baseCO2() | number: "1.0-0" }} kg CO2</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="font-semibold text-gray-900">{{ "contribute.summary.co2ToCompensate" | transloco }}</span>
            <span class="font-bold text-primary-600">
              {{ wizardService.co2ToCompensate() | number: "1.0-0" }} kg ({{ wizardService.co2ToCompensateTonnes() | number: "1.2-2" }} t)
            </span>
          </div>
          <div class="border-t border-greyscale-200 pt-3">
            <div class="flex items-center justify-between">
              <span class="text-gray-600">{{ "contribute.pricing.subtotal" | transloco }}</span>
              <span class="font-medium">{{ wizardService.contributionAmount() | number: "1.2-2" }} EUR</span>
            </div>
          </div>
          <div>
            <div class="flex items-center justify-between">
              <span class="text-gray-600">
                @if (wizardService.isReverseCharge()) {
                  {{ "contribute.pricing.vatReverseCharge" | transloco }}
                } @else {
                  {{ "contribute.pricing.vat" | transloco }} ({{ (wizardService.vatRate() * 100) | number: "1.0-0" }}%)
                }
              </span>
              <span class="font-medium">{{ wizardService.vatAmount() | number: "1.2-2" }} EUR</span>
            </div>
            @if (wizardService.isReverseCharge()) {
              <p class="mt-1 text-xs text-blue-600">
                {{ "contribute.pricing.reverseChargeNote" | transloco }}
              </p>
            }
          </div>
          <div class="border-t border-greyscale-200 pt-3">
            <div class="flex items-center justify-between">
              <span class="text-lg font-bold text-gray-900">{{ "contribute.pricing.total" | transloco }}</span>
              <span class="text-xl font-bold text-primary-600">{{ wizardService.totalAmount() | number: "1.2-2" }} EUR</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Navigation -->
  <div class="flex justify-between pt-4">
    <button
      pButton
      icon="pi pi-arrow-left"
      severity="secondary"
      (click)="onBack()"
    >
      {{ "common.back" | transloco }}
    </button>
    <button
      pButton
      icon="pi pi-arrow-right"
      iconPos="right"
      [disabled]="!wizardService.canProceedStep2()"
      (click)="onNext()"
    >
      {{ "common.next" | transloco }}
    </button>
  </div>
</div>
